import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { DpAppSwitcherConfigService, DpCardMenuPlacement, DpCardTitle, DpChartColor, DpChartModule, DpFilterCard, DpFilterCardModule, DpFilterOption, DpLineChart, DpPieChart, DpSectionFiltersOptions, DpThemeSwitcherService, DpThemeType } from '@docpath/webcomponents';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AlertRule } from '../../../configuration/alert-rules/models/alert-rule';
import { MonitoringResult, MonitoringSearch, SearchKeyType } from '../../models/monitoring';
import { ReferenceValues } from '../../models/reference-values.';
import { MonitoringConstants } from '../../monitoring-constants';
import { MonitoringService } from '../../services/monitoring.service';
import { MonitoringHttpService } from '../../services/monitoring-http.service';
import { MonitoringChartService } from './monitoring-chart.service';

@Component({
  selector: 'app-monitoring-chart',
  templateUrl: './monitoring-chart.component.html',
  standalone: true,
  imports: [DpFilterCardModule, DpChartModule]
})
export class MonitoringChartComponent implements OnInit, OnDestroy {

  private readonly i18nPrefix = 'monitoring.chart';

  @Input() dgeId!: number;

  theme = DpThemeType.Light;

  lineChartRam?: DpLineChart;
  lineChartCpu?: DpLineChart;
  pieChart?: DpPieChart;
  showLoadingCpu = true;
  showLoadingRam = true;
  showLoadingDisk = true;
  noResultsLabel = '';

  cardFiltersRam = [
    new DpFilterOption({
      label: `${this.i18nPrefix}.ram.filter.system.label`, value: MonitoringConstants.CHART_RAM_SYSTEM, tooltip: `${this.i18nPrefix}.ram.filter.system.tooltip`, active: true
    }),
    new DpFilterOption({
      label: `${this.i18nPrefix}.ram.filter.java.label`, value: MonitoringConstants.CHART_RAM_JAVA, tooltip: `${this.i18nPrefix}.ram.filter.java.tooltip`
    })
  ];

  cpuFilterCard: DpFilterCard = new DpFilterCard({
    title: new DpCardTitle({
      label: `${this.i18nPrefix}.cpu.label`,
      tooltip: `${this.i18nPrefix}.cpu.tooltip`
    }),
    bodyHeight: '350px'
  });

  ramFilterCard: DpFilterCard = new DpFilterCard({
    title: new DpCardTitle({
      label: `${this.i18nPrefix}.ram.label`,
      tooltip: `${this.i18nPrefix}.ram.tooltip`
    }),
    bodyHeight: '350px',
    menuPlacement: DpCardMenuPlacement.Left,
    sections: [new DpSectionFiltersOptions({ filters: this.cardFiltersRam })]
  });

  diskFilterCard: DpFilterCard = new DpFilterCard({
    title: new DpCardTitle({
      label: `${this.i18nPrefix}.disk.label`,
      tooltip: `${this.i18nPrefix}.disk.tooltip`
    }),
    bodyHeight: '350px'
  });

  monitoringRefreshChangedSubscription?: Subscription;

  cpuResult: MonitoringResult[] = [];
  ram: MonitoringResult[] = [];
  disk: MonitoringResult[] = [];
  referenceValues!: ReferenceValues;
  assignedAlertRules!: AlertRule[];

  private readonly monitoringHttpService = inject(MonitoringHttpService);
  private readonly monitoringService = inject(MonitoringService);
  private readonly monitoringChartService = inject(MonitoringChartService);
  private readonly translateService = inject(TranslateService);
  private readonly themeSwitcherService = inject(DpThemeSwitcherService);
  private readonly appSwitcherConfigService = inject(DpAppSwitcherConfigService);

  ngOnInit(): void {
    this.theme = this.appSwitcherConfigService.getStoredConfig().theme;
    this.noResultsLabel = this.translateService.instant('ext.filter-card.label.no-results');

    this.monitoringService.dgeChanged.subscribe(dgeId => {
      this.dgeId = dgeId;
      this.loadDataForCharts();
    });

    if (!this.monitoringRefreshChangedSubscription) {
      this.monitoringRefreshChangedSubscription = this.monitoringService.refreshChanged.subscribe(() => {
        this.loadDataForCharts();
      });
    }

    this.monitoringService.assignedRulesChanged.subscribe(() => {
      this.loadDataForCharts();
    });

    this.translateService.onLangChange.subscribe(() => {
      this.noResultsLabel = this.translateService.instant('ext.filter-card.label.no-results');
      this.loadChartSubtitles();
      this.monitoringChartService.loadPieSerieTranslatedText(this.pieChart?.echartOptions?.graphic);
    });

    this.themeSwitcherService.themeChanged.subscribe((theme: DpThemeType) => {
      this.theme = theme;
      this.monitoringChartService.changePieChartTheme(this.pieChart, theme);
    });

  }

  ngOnDestroy(): void {
    if (this.monitoringRefreshChangedSubscription) {
      this.monitoringRefreshChangedSubscription.unsubscribe();
    }
  }

  loadRamFilter(filter?: DpFilterOption): void {
    const activeFilter = filter ?? this.ramFilterCard.sections.find(section => section.filters.some(filter => filter.active))?.filters.find(filter => filter.active);

    this.ramFilterCard.subtitle = this.monitoringChartService.loadRamFilterCardSubtitle(this.referenceValues.ram, this.referenceValues.java, this.cardFiltersRam.find(filter => filter.active)?.value as string);
    this.loadRam(activeFilter?.value as string);
  }

  private loadDataForCharts(): void {
    if (this.dgeId) {
      this.showLoadingCpu = true;
      this.showLoadingRam = true;
      this.showLoadingDisk = true;

      this.monitoringChartService.getReferenceValues(this.dgeId).subscribe(referenceValues => {
        this.referenceValues = referenceValues;
        this.monitoringService.notifyReferenceValuesChange(this.referenceValues);
        this.monitoringChartService.getRulesAssignment(this.dgeId).subscribe(assignedAlertRules => {
          this.assignedAlertRules = assignedAlertRules.filter(rule => rule.assigned).map(rule => rule.alertRule);
          this.loadChartSubtitles();
          this.loadCpu();
          this.loadRam();
          this.loadDisk();
        });
      });
    }
  }

  private loadChartSubtitles() {
    this.cpuFilterCard.subtitle = this.monitoringChartService.loadCpuFilterCardSubtitle(this.referenceValues.cpu, this.referenceValues.cores);
    this.ramFilterCard.subtitle = this.monitoringChartService.loadRamFilterCardSubtitle(this.referenceValues.ram, this.referenceValues.java, this.cardFiltersRam.find(filter => filter.active)?.value as string);
    this.diskFilterCard.subtitle = this.referenceValues.partition;
  }

  private loadRam(filter?: string): void {
    const filterSelected = filter ?? this.cardFiltersRam.find(filter => filter.active)?.value;
    const searchKeyType = (filterSelected === MonitoringConstants.CHART_RAM_SYSTEM) ? SearchKeyType.Ram : SearchKeyType.Java;

    const search = new MonitoringSearch(searchKeyType, this.dgeId);

    if (!this.lineChartRam) {
      this.showLoadingRam = true;
    }

    this.monitoringHttpService.getMonitoring(search).subscribe(result => {
      this.ram = result;
      this.lineChartRam = this.monitoringChartService.initLineChart(this.assignedAlertRules, searchKeyType, this.ram, DpChartColor.Green, DpChartColor.Green);
      this.loadChartSubtitles();
      this.showLoadingRam = false;
    });
  }

  private loadCpu(): void {
    const search = new MonitoringSearch(SearchKeyType.Cpu, this.dgeId);

    if (!this.lineChartCpu) {
      this.showLoadingCpu = true;
    }

    this.monitoringHttpService.getMonitoring(search).subscribe(result => {
      this.cpuResult = result;
      this.lineChartCpu = this.monitoringChartService.initLineChart(this.assignedAlertRules, SearchKeyType.Cpu, this.cpuResult, DpChartColor.Blue, DpChartColor.Blue);
      this.showLoadingCpu = false;
    });
  }

  private loadDisk(): void {
    const search = new MonitoringSearch(SearchKeyType.Disk, this.dgeId);

    if (!this.pieChart) {
      this.showLoadingDisk = true;
    }

    this.monitoringHttpService.getMonitoring(search).subscribe(result => {
      this.disk = result;
      this.pieChart = this.monitoringChartService.initPieChart(this.disk, this.referenceValues, this.theme);
      this.showLoadingDisk = false;
    });
  }

}
